import {useMemo, useState} from 'react'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {useTranslation} from '@/translations/i18n'
import {Button} from '@/components/ui/button/Button'
import {Text} from '@/components/ui/text/Text'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {MailerFormValidationSchema, SendMailPayload} from '@/features/mailer/types'
import {MailerRecipientSection} from '@/features/mailer/components/mailer-recipient-section/MailerRecipientSection'
import {MailerComposeSection} from '@/features/mailer/components/mailer-compose-section/MailerComposeSection'
import {MailerRecipientsPreviewModal} from '@/features/mailer/components/mailer-recipients-preview-modal/MailerRecipientsPreviewModal'
import {MailerSendConfirmationDialog} from '@/features/mailer/components/mailer-send-confirmation-dialog/MailerSendConfirmationDialog'
import {MailerResetConfirmationModal} from '@/features/mailer/components/mailer-reset-confirmation-modal/MailerResetConfirmationModal'
import {useSendMail} from '@/features/mailer/queries/useSendMail'
import {useMailerLiveCount} from '@/features/mailer/hooks/useMailerLiveCount'
import {buildRecipientsPayload, plainTextBodyToHtml} from '@/features/mailer/utils'
import {IS_PRODUCTION_SEND_ENABLED, MAILER_TEST_RECIPIENTS} from '@/features/mailer/constants'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {errorHandler} from '@/utilities/helpers'
import {toast} from '@/utilities/toast/toast'
import {usePermissions} from '@/features/auth/store/store'
import {StyledMailerFormContent, StyledMailerFooter, StyledMailerFooterGroup} from '@/pages/mailer/style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Badge} from '@/components/ui/badge/Badge'
import {SearchSmIcon} from '@/components/ui/icon/Icon'

type SendMode = 'test' | 'production'

export const MailerForm = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const [attachments, setAttachments] = useState<File[]>([])
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [confirmMode, setConfirmMode] = useState<SendMode | null>(null)
    const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false)
    const [previewRecipients, setPreviewRecipients] = useState<SendMailPayload['recipients'] | null>(null)

    const form = useForm<MailerFormValidationSchema>({
        resolver: zodResolver(MailerFormValidationSchema),
        defaultValues: {
            subject: '',
            body: '',
            includePatients: false,
            includeConsultants: false,
            patientMinAge: undefined,
            patientNeuromuscularDiagnosisId: undefined,
            patientServiceType: undefined,
            consultantSpecializationId: undefined,
            consultantStatus: undefined,
            consultantContractType: undefined
        }
    })

    const liveCount = useMailerLiveCount(form)
    const includePatients = form.watch('includePatients')
    const includeConsultants = form.watch('includeConsultants')
    const subject = form.watch('subject')
    const body = form.watch('body')
    const showLiveCount = includePatients || includeConsultants

    const hasPatientFilters = useMemo(
        () =>
            !!(
                form.watch('patientMinAge') ||
                form.watch('patientMaxAge') ||
                form.watch('patientGender') ||
                form.watch('patientNeuromuscularDiagnosisId') ||
                form.watch('patientResidenceAslDistrictId') ||
                form.watch('patientOutsideRegion') ||
                form.watch('patientServiceType')
            ),
        [
            form.watch('patientMinAge'),
            form.watch('patientMaxAge'),
            form.watch('patientGender'),
            form.watch('patientNeuromuscularDiagnosisId'),
            form.watch('patientResidenceAslDistrictId'),
            form.watch('patientOutsideRegion'),
            form.watch('patientServiceType')
        ]
    )

    const hasConsultantFilters = useMemo(
        () =>
            !!(
                form.watch('consultantSpecializationId') ||
                form.watch('consultantStatus') ||
                form.watch('consultantContractType') ||
                form.watch('consultantWorkSector')
            ),
        [
            form.watch('consultantSpecializationId'),
            form.watch('consultantStatus'),
            form.watch('consultantContractType'),
            form.watch('consultantWorkSector')
        ]
    )

    const sendMutation = useSendMail({
        onError: errorHandler,
        onSuccess: data => {
            if (confirmMode === 'test') {
                toast.success(t('mailer:send_test_success', {count: MAILER_TEST_RECIPIENTS.length}))
            } else {
                toast.success(t('mailer:send_success', {count: data.recipientCount}))
            }
            queryClient.invalidateQueries({queryKey: [QUERY_KEYS.MAILER_BATCHES]})
            form.reset()
            setAttachments([])
            setConfirmMode(null)
        }
    })

    const handlePreview = () => {
        const formData = form.getValues()

        if (!formData.includePatients && !formData.includeConsultants) {
            form.trigger('includePatients')
            return
        }

        setPreviewRecipients(buildRecipientsPayload(formData))
        setIsPreviewOpen(true)
    }

    const openConfirmDialog = (mode: SendMode) => {
        form.handleSubmit(() => setConfirmMode(mode))()
    }

    const handleConfirmSend = () => {
        const formData = form.getValues()
        const recipients =
            confirmMode === 'test'
                ? {
                      consultants: {
                          filters: {
                              operator: 'and' as const,
                              fields: [{key: 'email', operator: 'in', value: [...MAILER_TEST_RECIPIENTS]}]
                          }
                      }
                  }
                : buildRecipientsPayload(formData)

        sendMutation.mutate({
            payload: {
                subject: confirmMode === 'test' ? `[TEST] ${formData.subject}` : formData.subject,
                body: plainTextBodyToHtml(formData.body),
                recipients
            },
            attachments
        })
    }

    const handleResetClick = () => {
        if (!form.formState.isDirty && attachments.length === 0) {
            performReset()
            return
        }
        setIsResetConfirmOpen(true)
    }

    const performReset = () => {
        form.reset()
        setAttachments([])
        setPreviewRecipients(null)
        setIsResetConfirmOpen(false)
    }

    const productionValidationReason = (() => {
        if (!IS_PRODUCTION_SEND_ENABLED) return t('mailer:production_send_disabled_tooltip')
        if (!subject) return t('mailer:validation_subject_missing')
        if (!body) return t('mailer:validation_body_missing')
        if (liveCount.isLoading) return t('mailer:validation_live_count_pending')
        if (liveCount.isError) return t('mailer:validation_live_count_error')
        if (liveCount.total === 0) return t('mailer:validation_no_recipients')
        return null
    })()

    const testValidationReason = (() => {
        if (!subject) return t('mailer:validation_subject_missing')
        if (!body) return t('mailer:validation_body_missing')
        return null
    })()

    const showAllRecipientsBanner =
        (includePatients && !hasPatientFilters) || (includeConsultants && !hasConsultantFilters)

    return (
        <>
            <StyledMailerFormContent>
                <MailerRecipientSection
                    form={form}
                    hasPatientFilters={hasPatientFilters}
                    hasConsultantFilters={hasConsultantFilters}
                    showAllRecipientsBanner={showAllRecipientsBanner}
                />

                {showLiveCount && (
                    <Flexbox align="center" gap={2}>
                        <Button type="button" variant="ghost" size="sm" onClick={handlePreview}>
                            <SearchSmIcon />
                            {t('mailer:view_recipients_list')}
                        </Button>
                        <Badge variant={liveCount.total > 0 ? 'primary' : 'orange'}>
                            {liveCount.isLoading ? (
                                <Spinner size={12} />
                            ) : liveCount.isError ? (
                                t('mailer:recipients_count_error')
                            ) : (
                                t('mailer:recipients_count', {count: liveCount.total})
                            )}
                        </Badge>
                    </Flexbox>
                )}

                <MailerComposeSection form={form} attachments={attachments} onAttachmentsChange={setAttachments} />
            </StyledMailerFormContent>

            <StyledMailerFooter>
                <StyledMailerFooterGroup>
                    <Button type="button" variant="ghost" onClick={handleResetClick}>
                        <Text size="textMd">{t('mailer:reset')}</Text>
                    </Button>
                </StyledMailerFooterGroup>
                {permissions?.settings?.write && (
                    <StyledMailerFooterGroup>
                        {testValidationReason ? (
                            <Tooltip content={testValidationReason} variant="light">
                                <Button type="button" variant="secondary" disabled>
                                    <Text size="textMd">{t('mailer:send_test')}</Text>
                                </Button>
                            </Tooltip>
                        ) : (
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => openConfirmDialog('test')}
                                disabled={sendMutation.isPending}
                            >
                                <Text size="textMd">{t('mailer:send_test')}</Text>
                            </Button>
                        )}
                        {productionValidationReason ? (
                            <Tooltip content={productionValidationReason} variant="light">
                                <Button type="button" variant="primaryNeutral" disabled>
                                    <Text size="textMd">{t('mailer:send')}</Text>
                                </Button>
                            </Tooltip>
                        ) : (
                            <Button
                                type="button"
                                variant="primaryNeutral"
                                onClick={() => openConfirmDialog('production')}
                                disabled={sendMutation.isPending}
                            >
                                <Text size="textMd">
                                    {sendMutation.isPending ? t('mailer:sending') : t('mailer:send')}
                                </Text>
                                {sendMutation.isPending && <Spinner />}
                            </Button>
                        )}
                    </StyledMailerFooterGroup>
                )}
            </StyledMailerFooter>

            {isPreviewOpen && previewRecipients && (
                <MailerRecipientsPreviewModal recipients={previewRecipients} onClose={() => setIsPreviewOpen(false)} />
            )}

            {confirmMode && (
                <MailerSendConfirmationDialog
                    mode={confirmMode}
                    testRecipients={[...MAILER_TEST_RECIPIENTS]}
                    onClose={() => setConfirmMode(null)}
                    onConfirm={handleConfirmSend}
                    isSending={sendMutation.isPending}
                    subject={subject}
                    body={body}
                    attachments={attachments}
                    liveCount={liveCount}
                    includePatients={includePatients}
                    includeConsultants={includeConsultants}
                    hasPatientFilters={hasPatientFilters}
                    hasConsultantFilters={hasConsultantFilters}
                />
            )}

            {isResetConfirmOpen && (
                <MailerResetConfirmationModal onClose={() => setIsResetConfirmOpen(false)} onConfirm={performReset} />
            )}
        </>
    )
}
